import { generateClient } from "aws-amplify/api";
import * as Queries from "../graphql/queries";
import { generateAction } from "../redux-helpers";
import { DASH_ALERTS, DASH_ACTIVE_FORMS, DASH_COMPLETED_FORMS, DASH_GRADUATION_DEADLINE_MAP, DASH_GRADUATION_DEADLINE_TERM, DASH_OVERVIEW, DASH_TASKS } from "../actions-index";
import FormLink from "../Common/components/FormLink";
import { gradSchool, programReview } from "./enums";
import { buildGraduationDeadlineMap } from "./transform";
import { ACTION_STATUS, APPROVAL_STATUS, APPROVAL_SUBSTATUS } from "../util/constants";
import { buildAction, formatDate } from "../util/functions";

const API = generateClient();

export function getDashboardAlerts(osuid) {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DASH_ALERTS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getStudentAlerts, variables: { osuid } });
            const alerts = (response?.data?.getStudentAlerts?.alerts ?? []);
            dispatch(buildAction(DASH_ALERTS, { alerts, status: ACTION_STATUS.SUCCESS }));
        } catch(error) {
            console.error("Dashboard Alerts Error", error);
            dispatch(buildAction(DASH_ALERTS, { alerts: [], status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getDashboardOverview() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DASH_OVERVIEW, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getDashboardOverview, variables: {} });
            const overview = (response?.data?.getDashboardOverview?.dashboardOverview ?? {});
            dispatch(buildAction(DASH_OVERVIEW, { overview, status: ACTION_STATUS.SUCCESS }));
        } catch(error) {
            console.error("Dashboard Overview Error", error);
            dispatch(buildAction(DASH_OVERVIEW, { overview: {}, status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getDashboardForms(filter = {}) {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DASH_ACTIVE_FORMS, { status: ACTION_STATUS.LOADING }));
            dispatch(buildAction(DASH_COMPLETED_FORMS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getForms, variables: { filter } });
            const forms = (response?.data?.getForms?.forms ?? []);
            const { activeForms = [], completedForms = [] } = iterateForms(forms);
            dispatch(buildAction(DASH_ACTIVE_FORMS, { forms: activeForms, status: ACTION_STATUS.SUCCESS }));
            dispatch(buildAction(DASH_COMPLETED_FORMS, { forms: completedForms, status: ACTION_STATUS.SUCCESS }));
        } catch(error) {
            console.error("Dashboard Forms Error", error);
            dispatch(buildAction(DASH_ACTIVE_FORMS, { forms: [], status: ACTION_STATUS.ERROR }));
            dispatch(buildAction(DASH_COMPLETED_FORMS, { forms: [], status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getDashboardTasks() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DASH_TASKS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getTasks, variables: { limit: 5 } });
            const tasks = (response?.data?.getTasks?.tasks ?? []);
            dispatch(buildAction(DASH_TASKS, { tasks, status: ACTION_STATUS.SUCCESS }));
        } catch(error) {
            console.error("Dashboard Tasks Error", error);
            dispatch(buildAction(DASH_TASKS, { tasks: [], status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getDashboardGraduationDeadlineMap() {
    return async (dispatch) => {
        try {
            dispatch(buildAction(DASH_GRADUATION_DEADLINE_MAP, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getGraduationDeadlines, variables: {} });
            const graduationDeadlines = (response?.data?.getGraduationDeadlines?.graduationDeadlines ?? {});
            const graduationDeadlineMap = buildGraduationDeadlineMap(graduationDeadlines);
            dispatch(buildAction(DASH_GRADUATION_DEADLINE_MAP, { graduationDeadlineMap, status: ACTION_STATUS.SUCCESS }));
        } catch(error) {
            console.error("Dashboard Graduation Deadline Map Error", error);
            dispatch(buildAction(DASH_GRADUATION_DEADLINE_MAP, { graduationDeadlineMap: {}, status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getStudentDashboard(filter = {}) {
    return async (dispatch) => {
        dispatch(getDashboardAlerts(filter?.studentid));
        dispatch(getDashboardForms(filter));
        dispatch(getDashboardGraduationDeadlineMap());
    };
}

export function getAdminDashboard() {
    return async (dispatch) => {
        dispatch(getDashboardOverview());
        dispatch(getDashboardTasks());
        dispatch(getDashboardGraduationDeadlineMap());
    };
}

export function getInquirerDashboard() {
    return async (dispatch) => {
        dispatch(getDashboardGraduationDeadlineMap());
    };
}

export function getProgramDashboard() {
    return async (dispatch) => {
        dispatch(getDashboardTasks());
        dispatch(getDashboardGraduationDeadlineMap());
    };
}

export function setDashboardGraduationDeadlineTerm(term) {
    return async (dispatch) => dispatch(buildAction(DASH_GRADUATION_DEADLINE_TERM, term));
}

function iterateForms(forms = []) {
    let completedForms = [], activeForms = []
    if (Array.isArray(forms)) {
        let filteredForms = forms.filter(form => form.id && form.formType)
        filteredForms.forEach((({ id, formType, submittedDate, lastUpdatedDate, status, substatus, completedDate }) => {            
            if([APPROVAL_STATUS.CANCELLED, APPROVAL_STATUS.COMPLETED, APPROVAL_STATUS.DENIED].includes(status)) {
                let formattedForm = {
                    name: {
                        Component: () => (
                            <FormLink
                                id={id}
                                view
                                formType={formType}
                            />
                        ),
                    },
                    completedDate: formatDate(completedDate, "LL/dd/yyyy")
                }
                completedForms.push(formattedForm)
            } else {
                let programReviewEnum = programReview.notStarted;
                let gradSchoolEnum = gradSchool.notStarted;
                if(status === APPROVAL_STATUS.PENDING) {
                    if(substatus === APPROVAL_SUBSTATUS.DEPT_PENDING) programReviewEnum = programReview.progress;
                    if(substatus === APPROVAL_SUBSTATUS.GS_PENDING) {
                        programReviewEnum = programReview.reviewed;
                        gradSchoolEnum = gradSchool.progress;
                    }
                }
                if(status === APPROVAL_STATUS.APPROVED) {
                    programReviewEnum = programReview.reviewed;
                    gradSchoolEnum = gradSchool.reviewed;
                }
                let formattedForm = {
                    name: {
                        Component: () => (
                            <FormLink
                                id={id}
                                view
                                formType={formType}
                            />
                        ),
                    },
                    submittedDate: (submittedDate ? formatDate(submittedDate, "LL/dd/yyyy") : "-"),
                    lastUpdatedDate: (lastUpdatedDate ? formatDate(lastUpdatedDate, "LL/dd/yyyy") : "-"),
                    programReview: programReviewEnum,
                    gradSchool: gradSchoolEnum
                }
                activeForms.push(formattedForm)
            }
        }))
    }
    return {
        completedForms,
        activeForms
    }
}

export function getStudentAlerts(osuid = "") {
    return async (dispatch, getState) => {
        dispatch(generateAction(DASH_ALERTS, {
            status: "loading"
        }))
        let variables = {}
        const { authentication } = getState()
        const allowOsuId = authentication?.user?.isGraduationAdmin
        if (allowOsuId && osuid) variables.osuid = osuid
        try {
            const response = await API.graphql({ query: Queries.getStudentAlerts, variables });
            const alerts = response?.data?.getStudentAlerts?.alerts
            dispatch(generateAction(DASH_ALERTS, {
                alerts: alerts || []
            }))
        } catch (error) {
            dispatch(generateAction(DASH_ALERTS, {
                status: "error"
            }))
        }
    }
}

export function getForms() {
    return async dispatch => {
        dispatch(generateAction(DASH_ACTIVE_FORMS, {
            status: "loading"
        }))
        dispatch(generateAction(DASH_COMPLETED_FORMS, {
            status: "loading"
        }))
        let filter = {}
        const response = await API.graphql({ query: Queries.getForms });

        const forms = response?.data?.getForms?.forms
        let activeForms = [], completedForms = []
        if (Array.isArray(forms)) {
            let filteredForms = forms.filter(form => form.id && form.formType)
            filteredForms.forEach((({ id, formType, submitted, lastUpdated, status }) => {
                let formattedForm = {
                    name: {
                        Component: () => (
                            <FormLink
                                id={id}
                                view
                                formType={formType}
                            />
                        ),
                    },
                    submittedDate: submitted || "-",
                    lastUpdatedDate: lastUpdated || "-",
                    programReview: programReview.notStarted,
                    gradSchool: gradSchool.notStarted,
                }
                if(status === 'COMPLETE') {
                    completedForms.push(formattedForm)
                } else {
                    activeForms.push(formattedForm)
                }
            }))
        }
        dispatch(generateAction(DASH_ACTIVE_FORMS, {
            forms: activeForms
        }))
        dispatch(generateAction(DASH_COMPLETED_FORMS, {
            forms: completedForms
        }))
    }
}